export interface CommissionTier {
  min_cents: number | bigint;
  max_cents: number | bigint | null;
  rate_bps: number;
}

export interface GrossUpResult {
  totalCents: number;
  marginCents: number;
  rateBps: number;
}

/**
 * Platform default tiered commission matrix:
 * - Under $1k: 25.0% (2500 bps)
 * - $1k - $3k: 20.0% (2000 bps)
 * - $3k - $5k: 17.0% (1700 bps)
 * - $5k - $8k: 15.0% (1500 bps)
 * - Over $8k: 13.0% (1300 bps)
 */
export const DEFAULT_COMMISSION_TIERS: CommissionTier[] = [
  { min_cents: 0, max_cents: 100000, rate_bps: 2500 },
  { min_cents: 100001, max_cents: 300000, rate_bps: 2000 },
  { min_cents: 300001, max_cents: 500000, rate_bps: 1700 },
  { min_cents: 500001, max_cents: 800000, rate_bps: 1500 },
  { min_cents: 800001, max_cents: null, rate_bps: 1300 },
];

/**
 * Filter out invalid tiers (rate_bps >= 10000 or negative rate_bps).
 * Normalizes min_cents and max_cents to numbers.
 */
function sanitizeTiers(tiers: CommissionTier[]): Array<{ min_cents: number; max_cents: number | null; rate_bps: number }> {
  if (!Array.isArray(tiers) || tiers.length === 0) {
    return sanitizeTiers(DEFAULT_COMMISSION_TIERS);
  }
  const valid = tiers
    .filter((t) => t.rate_bps != null && t.rate_bps >= 0 && t.rate_bps < 10000)
    .map((t) => ({
      min_cents: Number(t.min_cents),
      max_cents: t.max_cents != null ? Number(t.max_cents) : null,
      rate_bps: Math.round(t.rate_bps),
    }));

  if (valid.length === 0) {
    return [{ min_cents: 0, max_cents: null, rate_bps: 2000 }];
  }
  return valid;
}

/**
 * Looks up the applicable commission rate in basis points for a given total ex-GST cents.
 * Mirrors Postgres `get_commission_rate_bps`.
 */
export function getCommissionRateBpsFromTiers(
  totalCents: number,
  tiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS
): number {
  const sanitized = sanitizeTiers(tiers);
  const lookupCents = Math.max(0, Math.round(totalCents || 0));

  // Sort by min_cents DESC to match PostgreSQL behavior: ORDER BY min_cents DESC LIMIT 1
  const sorted = [...sanitized].sort((a, b) => b.min_cents - a.min_cents);

  for (const tier of sorted) {
    if (lookupCents >= tier.min_cents && (tier.max_cents === null || lookupCents <= tier.max_cents)) {
      return tier.rate_bps;
    }
  }

  // Mirror Postgres get_commission_rate_bps: a customer matrix with a gap falls through to the
  // platform default tiers, and only then to the 20% floor.
  if (tiers !== DEFAULT_COMMISSION_TIERS) {
    return getCommissionRateBpsFromTiers(lookupCents, DEFAULT_COMMISSION_TIERS);
  }
  return 2000;
}

/**
 * Decide which tiers govern an existing job:
 * if an explicit manual override is set on the job, return a single-tier flat rate;
 * otherwise return the customer's tiers (or default tiers).
 */
export function resolveEffectiveTiers(
  overrideBps: number | null | undefined,
  customerTiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS
): { tiers: CommissionTier[]; overrideRateBps: number | null } {
  const rounded = overrideBps != null && Number.isFinite(overrideBps) ? Math.round(overrideBps) : null;
  // 0-9999 mirrors the jobs.commission_override_bps CHECK; 10000+ would divide by zero on gross-up.
  if (rounded != null && rounded >= 0 && rounded <= 9999) {
    return {
      tiers: [{ min_cents: 0, max_cents: null, rate_bps: rounded }],
      overrideRateBps: rounded,
    };
  }
  return { tiers: customerTiers, overrideRateBps: null };
}

/**
 * Grosses up a client job cost (excluding platform margin, ex GST) in integer cents
 * to find the total job value (including platform margin, ex GST).
 *
 * Formula: total = client_job_cost / (1 - rate), where rate applies to `total`.
 *
 * Algorithm:
 * 1. For each valid tier compute candidate total = round(exMargin / (1 - rate_bps / 10000)).
 * 2. Accept candidate(s) whose candidate total falls inside that tier's [min_cents, max_cents] band.
 * 3. If multiple match, pick the lowest total.
 * 4. If none match (band gap), iterate rate(total) until stable (max 10 iterations).
 */
/**
 * Calculates the payout to the trade for a given grossed total and tiers.
 * payout(T) = T - round(T * rateFromTiers(T) / 10000)
 */
export function calculatePayoutCents(
  totalCents: number,
  tiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS
): number {
  const rateBps = getCommissionRateBpsFromTiers(totalCents, tiers);
  const marginCents = Math.round((totalCents * rateBps) / 10000);
  return totalCents - marginCents;
}

/**
 * Grosses up a client job cost (excluding platform margin, ex GST) in integer cents
 * to find the total job value (including platform margin, ex GST).
 *
 * Single Tier-Gap Rule:
 * For each valid tier t:
 *   candidate = max(ceil(exMargin / (1 - t.rate_bps/10000)), t.min_cents).
 *   Keep it only if candidate is inside t's band (max_cents null or candidate <= max_cents)
 *   AND payout(candidate) >= exMargin where payout(T) = T - round(T * rateFromTiers(T)/10000).
 * Return candidate with smallest totalCents;
 * rateBps = rateFromTiers(total); marginCents = total - exMargin.
 * Fallback to top tier if none qualifies.
 */
export function grossUpClientTotalCents(
  exMarginCents: number | null | undefined,
  tiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS
): GrossUpResult {
  const validTiers = sanitizeTiers(tiers);

  // Negative or null/NaN amounts collapse to 0
  if (exMarginCents == null || isNaN(exMarginCents) || exMarginCents <= 0) {
    const rateBps = getCommissionRateBpsFromTiers(0, validTiers);
    return {
      totalCents: 0,
      marginCents: 0,
      rateBps,
    };
  }

  const exMargin = Math.round(exMarginCents);
  const candidates: number[] = [];

  for (const tier of validTiers) {
    let candidate = Math.max(
      Math.ceil(exMargin / (1 - tier.rate_bps / 10000)),
      tier.min_cents
    );

    // If candidate - 1 is still inside the tier band and satisfies payout, use candidate - 1
    // to provide the minimal consistent integer total.
    while (
      candidate - 1 >= tier.min_cents &&
      calculatePayoutCents(candidate - 1, validTiers) >= exMargin
    ) {
      candidate--;
    }

    const inMax = tier.max_cents === null || candidate <= tier.max_cents;
    if (inMax && calculatePayoutCents(candidate, validTiers) >= exMargin) {
      candidates.push(candidate);
    }
  }

  if (candidates.length > 0) {
    // Return candidate with smallest totalCents
    candidates.sort((a, b) => a - b);
    const totalCents = candidates[0];
    const rateBps = getCommissionRateBpsFromTiers(totalCents, validTiers);
    return {
      totalCents,
      marginCents: totalCents - exMargin,
      rateBps,
    };
  }

  // Defensive fallback to top tier (tier with highest min_cents or null max_cents)
  const topTier = [...validTiers].sort((a, b) => b.min_cents - a.min_cents)[0];
  let fallbackTotal = Math.max(
    Math.ceil(exMargin / (1 - topTier.rate_bps / 10000)),
    topTier.min_cents
  );
  while (
    fallbackTotal - 1 >= topTier.min_cents &&
    calculatePayoutCents(fallbackTotal - 1, validTiers) >= exMargin
  ) {
    fallbackTotal--;
  }

  const fallbackRateBps = getCommissionRateBpsFromTiers(fallbackTotal, validTiers);
  return {
    totalCents: fallbackTotal,
    marginCents: fallbackTotal - exMargin,
    rateBps: fallbackRateBps,
  };
}

/**
 * Inverse calculation for edit-mode back-derivation:
 * netOfMarginCents(totalCents, tiers) = total - round(total * rate / 10000)
 */
export function netOfMarginCents(
  totalCents: number | null | undefined,
  tiers: CommissionTier[] = DEFAULT_COMMISSION_TIERS
): number {
  if (totalCents == null || isNaN(totalCents) || totalCents <= 0) {
    return 0;
  }
  return Math.max(0, calculatePayoutCents(Math.round(totalCents), tiers));
}

export interface QuantityPricingItemInput {
  clientRate?: number | null;
  estimatedQuantity?: number | null;
}

export interface GrossUpQuantityPricingParams {
  clientCallOutFee?: number | null;
  items: QuantityPricingItemInput[];
  tiers?: CommissionTier[];
}

export interface DerivedQuantityPricingItem {
  clientRate: number | null;
  rate: number;
  estimatedQuantity: number;
  lineTotal: number;
}

export interface GrossUpQuantityPricingResult {
  minimumCallOutFee: number | null;
  items: DerivedQuantityPricingItem[];
  totalCents: number;
  marginCents: number;
  rateBps: number;
  estimatedValue: number;
  breakdown: {
    callOutDollars: number;
    unitsDollars: number;
  };
}

/**
 * Grosses up quantity-based pricing (call-out fee + line items) based on total ex-margin value.
 *
 * Algorithm:
 * 1. exMarginTotalCents = round(clientCallOutFee * 100) + sum(round(item.clientRate * 100) * qty).
 * 2. grossUpClientTotalCents(exMarginTotalCents, tiers) yields { totalCents, marginCents, rateBps }.
 * 3. rate = round(clientRate / (1 - rateBps / 10000)) per line.
 * 4. minimum_call_out_fee = round(clientCallOutFee / (1 - rateBps / 10000)).
 * 5. estimatedValue = derived callout + sum(derived line totals).
 */
export function grossUpQuantityPricing({
  clientCallOutFee,
  items,
  tiers = DEFAULT_COMMISSION_TIERS,
}: GrossUpQuantityPricingParams): GrossUpQuantityPricingResult {
  const validTiers = tiers;
  const calloutExCents =
    clientCallOutFee != null && !isNaN(clientCallOutFee) && clientCallOutFee > 0
      ? Math.round(clientCallOutFee * 100)
      : 0;

  const normalized = items.map((item) => {
    const cRate = item.clientRate != null && !isNaN(item.clientRate) ? item.clientRate : null;
    const qty =
      item.estimatedQuantity != null && !isNaN(item.estimatedQuantity) && item.estimatedQuantity > 0
        ? item.estimatedQuantity
        : 0;
    const rateExCents = cRate != null && cRate > 0 ? Math.round(cRate * 100) : 0;
    return { cRate, qty, rateExCents };
  });

  const linesExCents = normalized.reduce((sum, n) => sum + n.rateExCents * n.qty, 0);
  const exMarginTotalCents = calloutExCents + linesExCents;

  // Resolve the tier on the TOTAL (mirrors submission + backend), including the tier-gap rule.
  const grossed = grossUpClientTotalCents(exMarginTotalCents, validTiers);

  // Gross every line by the EFFECTIVE multiplier (total / ex-margin sum) rather than 1/(1-rate):
  // in a tier-gap band the total is bumped to the next tier's floor, and only the effective
  // multiplier makes the line items sum back to that total so the backend applies the same rate.
  const safeRateBps = grossed.rateBps >= 10000 || grossed.rateBps < 0 ? 0 : grossed.rateBps;
  const multiplier = exMarginTotalCents > 0
    ? grossed.totalCents / exMarginTotalCents
    : 1 / (1 - safeRateBps / 10000);

  let calloutIncCents = calloutExCents > 0 ? Math.round(calloutExCents * multiplier) : 0;
  const lineIncCents = normalized.map((n) => (n.rateExCents > 0 ? Math.round(n.rateExCents * multiplier) : 0));

  const sumCents = () =>
    Math.round(calloutIncCents + normalized.reduce((sum, n, i) => sum + lineIncCents[i] * n.qty, 0));

  // Per-line cent rounding can leave the sum a few cents below the grossed total. That only
  // matters when it drops the sum into a lower tier band; top it up so the backend rate matches.
  let totalCents = sumCents();
  if (totalCents < grossed.totalCents && getCommissionRateBpsFromTiers(totalCents, validTiers) !== grossed.rateBps) {
    const shortfall = grossed.totalCents - totalCents;
    if (calloutIncCents > 0) {
      calloutIncCents += shortfall;
    } else {
      const idx = normalized.findIndex((n, i) => n.qty > 0 && lineIncCents[i] > 0);
      if (idx >= 0) lineIncCents[idx] += Math.ceil(shortfall / normalized[idx].qty);
    }
    totalCents = sumCents();
  }

  const rateBps = getCommissionRateBpsFromTiers(totalCents, validTiers);
  const marginCents = totalCents - exMarginTotalCents;

  const derivedItems: DerivedQuantityPricingItem[] = normalized.map((n, i) => ({
    clientRate: n.cRate,
    rate: lineIncCents[i] / 100,
    estimatedQuantity: n.qty,
    lineTotal: (lineIncCents[i] * n.qty) / 100,
  }));

  const derivedCallout =
    calloutExCents > 0 ? calloutIncCents / 100 : clientCallOutFee === 0 ? 0 : null;
  const callOutDollars = derivedCallout ?? 0;
  const unitsDollars = (totalCents - calloutIncCents) / 100;

  return {
    minimumCallOutFee: derivedCallout,
    items: derivedItems,
    totalCents,
    marginCents,
    rateBps,
    estimatedValue: totalCents / 100,
    breakdown: {
      callOutDollars,
      unitsDollars,
    },
  };
}

/**
 * Inverts an inc-margin rate or fee back to ex-margin using a given rate in basis points.
 */
export function netOfMarginRate(
  incMarginDollars: number | null | undefined,
  rateBps: number
): number | null {
  if (incMarginDollars == null || isNaN(incMarginDollars)) return null;
  return Math.round(incMarginDollars * (1 - rateBps / 10000) * 100) / 100;
}

